/**
 * Dummy data — 결제
 * API_DOCS: 4. 결제 (서비스) / 29. 관리자 - 거래/매출
 *
 * PaymentResponse:
 *   id(int), reservation_id(int?), payment_type(string?), payment_key(string?),
 *   amount(decimal?), currency(string="KRW"), status(string), paid_at(datetime?), created_at(datetime)
 *
 * PaymentPrepareResponse: order_id(string), amount(float), order_name(string)
 * CardResponse: id(int), pg_provider(string?), card_last4(string?), card_brand(string?), is_default(boolean)
 * ReceiptResponse: payment_id(int), amount(float), status(string)
 * TransactionSummary: total_revenue(decimal), total_count(int), total_refund(decimal), net_revenue(decimal)
 */

const payments = [
  { id: 1, reservation_id: 1, payment_type: 'card', payment_key: 'pk_test_001', amount: 198000, currency: 'KRW', status: 'paid', paid_at: '2026-01-29T20:40:00', created_at: '2026-01-29T20:39:00' },
  { id: 2, reservation_id: 2, payment_type: 'card', payment_key: 'pk_test_002', amount: 300000, currency: 'KRW', status: 'paid', paid_at: '2026-01-30T14:20:00', created_at: '2026-01-30T14:19:00' },
  { id: 3, reservation_id: 6, payment_type: 'card', payment_key: 'pk_test_003', amount: 100000, currency: 'KRW', status: 'refunded', paid_at: '2026-02-04T18:00:00', created_at: '2026-02-04T17:59:00' },
  { id: 4, reservation_id: 4, payment_type: 'card', payment_key: 'pk_test_004', amount: 150000, currency: 'KRW', status: 'failed', paid_at: null, created_at: '2026-02-02T11:30:00' },
  { id: 5, reservation_id: 5, payment_type: 'card', payment_key: 'pk_test_005', amount: 120000, currency: 'KRW', status: 'cancelled', paid_at: null, created_at: '2026-02-03T09:00:00' },
];

const cards = [
  { id: 1, pg_provider: 'portone', card_last4: '1234', card_brand: 'visa', is_default: true },
  { id: 2, pg_provider: 'portone', card_last4: '5678', card_brand: 'mastercard', is_default: false },
];

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/* ── 서비스 (유저용) ── */

/** POST /api/payments/prepare */
export const preparePayment = async (data) => {
  await delay(300);
  return { order_id: `order_${Date.now()}`, amount: 198000, order_name: '프라이빗 이동 서비스' };
};

/** POST /api/payments/confirm */
export const confirmPayment = async (data) => {
  await delay(400);
  return { id: payments.length + 1, reservation_id: null, payment_type: 'card', payment_key: data.payment_key, amount: data.amount, currency: 'KRW', status: 'paid', paid_at: new Date().toISOString(), created_at: new Date().toISOString() };
};

/** GET /api/payments/cards */
export const getCards = async () => {
  await delay(200);
  return cards;
};

/** POST /api/payments/cards */
export const registerCard = async (data) => {
  await delay(400);
  const card = { id: cards.length + 1, pg_provider: data.pg_provider, card_last4: data.card_last4, card_brand: data.card_brand || null, is_default: data.is_default || false };
  cards.push(card);
  return card;
};

/** DELETE /api/payments/cards/:cardId */
export const deleteCard = async (cardId) => {
  await delay(300);
  const idx = cards.findIndex((c) => c.id === Number(cardId));
  if (idx === -1) throw new Error('카드를 찾을 수 없습니다');
  cards.splice(idx, 1);
  return { message: '카드가 삭제되었습니다' };
};

/** GET /api/payments/:id */
export const getPaymentById = async (id) => {
  await delay(200);
  const found = payments.find((p) => p.id === Number(id));
  if (!found) throw new Error('결제 정보를 찾을 수 없습니다');
  return found;
};

/** POST /api/payments/:id/refund */
export const refundPayment = async (id, data = {}) => {
  await delay(500);
  const found = payments.find((p) => p.id === Number(id));
  if (!found) throw new Error('결제 정보를 찾을 수 없습니다');
  found.status = 'refunded';
  return { message: '환불이 완료되었습니다' };
};

/** GET /api/payments/:id/receipt */
export const getReceipt = async (id) => {
  await delay(200);
  const found = payments.find((p) => p.id === Number(id));
  if (!found) throw new Error('결제 정보를 찾을 수 없습니다');
  return { payment_id: found.id, amount: found.amount, status: found.status };
};

/** POST /api/payments/:id/receipt/email */
export const sendReceiptEmail = async (id, email) => {
  await delay(300);
  return { message: '영수증이 발송되었습니다' };
};

/* ── 관리자 ── */

/** GET /api/admin/transactions (페이지네이션) */
export const getAdminTransactions = async (params = {}) => {
  await delay(300);
  const page = params.page || 1;
  const size = params.size || 20;
  const start = (page - 1) * size;
  return {
    items: payments.slice(start, start + size),
    total: payments.length,
    page,
    size,
    pages: Math.ceil(payments.length / size),
  };
};

/** GET /api/admin/transactions/summary */
export const getAdminTransactionSummary = async () => {
  await delay(300);
  const paid = payments.filter((p) => p.status === 'paid');
  const refunded = payments.filter((p) => p.status === 'refunded');
  const totalRevenue = paid.reduce((sum, p) => sum + p.amount, 0);
  const totalRefund = refunded.reduce((sum, p) => sum + p.amount, 0);
  return { total_revenue: totalRevenue, total_count: payments.length, total_refund: totalRefund, net_revenue: totalRevenue - totalRefund };
};

/** GET /api/admin/transactions/export (returns blob) */
export const exportAdminTransactions = async () => {
  await delay(500);
  return new Blob(['dummy xlsx data'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

/** GET /api/admin/transactions/:id */
export const getAdminTransactionById = async (id) => {
  await delay(200);
  const found = payments.find((p) => p.id === Number(id));
  if (!found) throw new Error('거래를 찾을 수 없습니다');
  return found;
};

/** POST /api/admin/payments/:id/refund — 관리자 환불 처리 */
export const adminRefund = async (id, data = {}) => {
  await delay(500);
  const found = payments.find((p) => p.id === Number(id));
  if (!found) throw new Error('결제 정보를 찾을 수 없습니다');
  found.status = 'refunded';
  return {
    payment_id: found.id,
    status: 'refunded',
    refunded_at: new Date().toISOString(),
    amount: found.amount,
    message: '환불이 완료되었습니다',
  };
};
